/**
 * Estructuracion de texto plano pegado.
 *
 * Es deliberadamente **heuristica y determinista**, no un modelo de lenguaje:
 * no hay llamadas de red, no hay claves, no hay coste y el resultado es
 * siempre reproducible. Detecta patrones que ya son estructura semantica
 * (listas con vinetas, numeracion, preguntas, lineas cortas que cierran un
 * bloque) y propone una estructura.
 *
 * Nada se aplica solo. La accion "Estructurar contenido" devuelve una
 * propuesta que el usuario revisa y acepta bloque por bloque.
 */

import { cleanPastedPlainText, normalizeNewlines } from "./text";
import { EMPTY_DOC, paragraph } from "./document";
import { parseInlineMarkdown } from "./markdown";
import type { RichDoc, RichNode } from "./types";

export type ProposalKind = "heading2" | "heading3" | "list" | "quote" | "divider" | "paragraph";

export interface StructureBlock {
  kind: ProposalKind;
  text: string;
  /** Razon por la que se propone este tipo. Se muestra en la revision. */
  reason: string;
}

export interface StructureProposal {
  blocks: StructureBlock[];
  /** Resumen legible para la revision previa. */
  summary: { headings: number; lists: number; paragraphs: number };
}

const BULLET_PATTERN = /^[-*•·‣▪–]\s+/;
const ORDERED_PATTERN = /^(?:\d+[.)]|[\u2022\u00b7])\s+/;
const MARKDOWN_HEADING_PATTERN = /^(#{1,6})\s+(.*)$/;
const DIVIDER_PATTERN = /^\s*(?:-{3,}|\*{3,}|_{3,})\s*$/;
const QUOTE_PATTERN = /^\s*>/;

interface RawBlock {
  lines: string[];
  /** Bloques que son una unidad semantica y no deben fusionarse con otros. */
  kind?: "divider" | "quote";
}

function splitBlocks(source: string): RawBlock[] {
  const normalized = normalizeNewlines(source).replace(/\r/g, "");
  const blocks: RawBlock[] = [];
  let current: string[] = [];
  let currentKind: RawBlock["kind"];

  const flush = () => {
    if (current.length > 0) blocks.push(currentKind ? { lines: current, kind: currentKind } : { lines: current });
    current = [];
    currentKind = undefined;
  };

  const lines = normalized.split("\n");
  let index = 0;
  while (index < lines.length) {
    const line = lines[index] ?? "";

    if (DIVIDER_PATTERN.test(line)) {
      flush();
      blocks.push({ lines: [line], kind: "divider" });
      index += 1;
      continue;
    }

    if (QUOTE_PATTERN.test(line)) {
      if (currentKind !== "quote") flush();
      currentKind = "quote";
      current.push(line);
      index += 1;
      continue;
    }

    const isListItem = BULLET_PATTERN.test(line.trim()) || ORDERED_PATTERN.test(line.trim());
    if (isListItem) {
      if (current.length > 0 && !current.every((item) => item.trim() === "")) {
        // Una lista puede continuar parrafos previos solo si son lista tambien.
        const previousAreListItems = current.every((item) => {
          const trimmed = item.trim();
          return BULLET_PATTERN.test(trimmed) || ORDERED_PATTERN.test(trimmed);
        });
        if (!previousAreListItems) flush();
      }
      current.push(line);
      index += 1;
      continue;
    }

    if (line.trim() === "") {
      flush();
      index += 1;
      continue;
    }

    // Una linea normal cierra una cita acumulada.
    if (currentKind === "quote") flush();

    current.push(line);
    index += 1;
  }
  flush();
  return blocks;
}

function isQuestion(line: string): boolean {
  return /[?¿]\s*$/.test(line.trim());
}

function looksLikeSubtitle(line: string): boolean {
  const value = line.trim();
  if (!value) return false;
  if (value.length > 90) return false;
  if (isQuestion(value)) return true;
  // Frases cortas que terminan en dos puntos introducen una lista o un bloque.
  if (/:$/.test(value) && value.length <= 70) return true;
  // Titulos en mayusculas sostenidas.
  const letters = value.replace(/[^a-zA-ZáéíóúñÁÉÍÓÚÑ]/g, "");
  if (letters.length >= 8 && letters === letters.toUpperCase()) return true;
  return false;
}

function stripMarkdownHeading(line: string): { level: number; value: string } | null {
  const match = MARKDOWN_HEADING_PATTERN.exec(line.trim());
  if (!match) return null;
  const hashes = match[1] ?? "";
  const value = (match[2] ?? "").trim();
  if (!value) return null;
  return { level: hashes.length, value };
}

/** Une las lineas de un bloque en un solo parrafo. */
function joinLines(lines: string[]): string {
  return lines
    .map((line) => line.trim())
    .join(" ")
    .replace(/\s+/g, " ")
    .trim();
}

/**
 * Analiza texto plano (o un doc sin estructura) y propone bloques.
 * No modifica el original: el admin decide que aplicar.
 */
export function proposeStructure(source: string | RichDoc): StructureProposal {
  const textSource = typeof source === "string" ? source : "";
  const blocks: StructureBlock[] = [];

  if (typeof source !== "string") {
    // Un doc ya estructurado se recorre igual: cada nodo de bloque es una unidad.
    for (const node of source.content) {
      const plain = nodeText(node);
      if (node.type === "horizontalRule") {
        blocks.push({ kind: "divider", text: "", reason: "Separador existente" });
        continue;
      }
      if (!plain) continue;
      if (node.type === "heading") {
        const level = node.attrs?.level === 3 ? 3 : 2;
        blocks.push({
          kind: level === 3 ? "heading3" : "heading2",
          text: plain,
          reason: "Ya es un subtitulo en el documento",
        });
        continue;
      }
      if (node.type === "blockquote") {
        blocks.push({ kind: "quote", text: plain, reason: "Ya es una cita en el documento" });
        continue;
      }
      if (node.type === "bulletList" || node.type === "orderedList") {
        blocks.push({
          kind: "list",
          text: plain,
          reason: "Ya es una lista en el documento",
        });
        continue;
      }
      blocks.push({ kind: "paragraph", text: plain, reason: "Parrafo existente" });
    }
  } else {
    const cleaned = cleanPastedPlainText(textSource);
    for (const raw of splitBlocks(cleaned)) {
      if (raw.kind === "divider") {
        blocks.push({ kind: "divider", text: "", reason: "Separador horizontal detectado" });
        continue;
      }
      if (raw.kind === "quote") {
        const quote = raw.lines
          .map((line) => line.replace(/^\s*>\s?/, "").trim())
          .filter(Boolean)
          .join(" ");
        if (quote) blocks.push({ kind: "quote", text: quote, reason: "Lineas con > (cita)" });
        continue;
      }

      // Markdown explicito gana sobre cualquier heuristica.
      const explicitHeadings = raw.lines
        .map(stripMarkdownHeading)
        .filter((value): value is { level: number; value: string } => value !== null);

      if (explicitHeadings.length === raw.lines.length && explicitHeadings.length > 0) {
        for (const heading of explicitHeadings) {
          // Un "#" unico venia a ser el titulo, que ahora es el H1 del post.
          const level = heading.level <= 1 ? 2 : heading.level === 2 ? 2 : 3;
          blocks.push({
            kind: level === 2 ? "heading2" : "heading3",
            text: heading.value,
            reason: heading.level <= 1 ? "Markdown # (se propone como H2)" : "Markdown ## o superior",
          });
        }
        continue;
      }

      const listLines = raw.lines.filter((line) => {
        const trimmed = line.trim();
        return BULLET_PATTERN.test(trimmed) || ORDERED_PATTERN.test(trimmed);
      });

      if (listLines.length === raw.lines.length && listLines.length > 0) {
        const isOrdered = ORDERED_PATTERN.test(listLines[0]?.trim() ?? "");
        blocks.push({
          kind: "list",
          text: listLines
            .map((line) => line.trim().replace(BULLET_PATTERN, "").replace(ORDERED_PATTERN, ""))
            .join("\n"),
          reason: isOrdered ? "Lineas numeradas detectadas" : "Lineas con vineta detectadas",
        });
        continue;
      }

      const joined = joinLines(raw.lines);
      if (!joined) continue;

      if (raw.lines.length === 1 && looksLikeSubtitle(raw.lines[0] ?? "")) {
        const line = (raw.lines[0] ?? "").trim();
        blocks.push({
          kind: isQuestion(line) ? "heading2" : "heading2",
          text: line.replace(/:$/, ""),
          reason: isQuestion(line)
            ? "Linea corta en forma de pregunta"
            : "Linea corta tipo introduccion de bloque",
        });
        continue;
      }

      blocks.push({ kind: "paragraph", text: joined, reason: "Parrafo de contenido" });
    }
  }

  return {
    blocks,
    summary: {
      headings: blocks.filter((block) => block.kind === "heading2" || block.kind === "heading3").length,
      lists: blocks.filter((block) => block.kind === "list").length,
      paragraphs: blocks.filter(
        (block) => block.kind === "paragraph" || block.kind === "quote" || block.kind === "divider",
      ).length,
    },
  };
}

/**
 * Convierte la propuesta aceptada en un doc real.
 * El usuario puede descartar bloques individuales antes de aplicar.
 */
export function proposalToDoc(accepted: StructureBlock[]): RichDoc {
  const nodes: RichNode[] = [];
  let pendingListItems: string[] = [];
  let pendingOrdered = false;

  const flushList = () => {
    if (pendingListItems.length === 0) return;
    const items = pendingListItems;
    const ordered = pendingOrdered;
    pendingListItems = [];
    nodes.push(
      ordered
        ? {
            type: "orderedList",
            content: items.map((item) => ({
              type: "listItem",
              content: [paragraph(...parseInlineMarkdown(item))],
            })),
          }
        : {
            type: "bulletList",
            content: items.map((item) => ({
              type: "listItem",
              content: [paragraph(...parseInlineMarkdown(item))],
            })),
          },
    );
  };

  for (const block of accepted) {
    const value = block.text.trim();

    if (block.kind === "divider") {
      flushList();
      nodes.push({ type: "horizontalRule" });
      continue;
    }

    if (!value) continue;

    if (block.kind === "quote") {
      flushList();
      nodes.push({ type: "blockquote", content: [paragraph(...parseInlineMarkdown(value))] });
      continue;
    }

    if (block.kind === "list") {
      const items = value
        .split("\n")
        .map((line) => line.trim())
        .filter(Boolean);
      if (items.length === 0) continue;
      pendingOrdered = /^\d+[.)]/.test(value.trim());
      pendingListItems = items;
      flushList();
      continue;
    }

    flushList();

    if (block.kind === "heading2")
      nodes.push({ type: "heading", attrs: { level: 2 }, content: parseInlineMarkdown(value) });
    else if (block.kind === "heading3")
      nodes.push({ type: "heading", attrs: { level: 3 }, content: parseInlineMarkdown(value) });
    else nodes.push(paragraph(...parseInlineMarkdown(value)));
  }

  flushList();
  return nodes.length > 0 ? { type: "doc", content: nodes } : EMPTY_DOC;
}

function nodeText(node: RichNode): string {
  if (node.type === "text") return node.text ?? "";
  if (node.type === "faqItem") return String(node.attrs?.question ?? "");
  const parts: string[] = [];
  const walk = (current: RichNode) => {
    if (current.type === "text" && current.text) parts.push(current.text);
    current.content?.forEach(walk);
  };
  node.content?.forEach(walk);
  return parts.join(" ").replace(/\s+/g, " ").trim();
}

/** Texto plano del doc, para offerser "estructurar" sobre contenido ya guardado. */
export function docAsPlainSource(doc: RichDoc): string {
  return doc.content
    .map((node) => {
      if (node.type === "bulletList" || node.type === "orderedList") {
        return (node.content ?? []).map((item) => `- ${nodeText(item)}`).join("\n");
      }
      if (node.type === "heading") return `\n## ${nodeText(node)}\n`;
      return nodeText(node);
    })
    .filter(Boolean)
    .join("\n\n");
}
